// @ts-nocheck
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { DashboardLayout } from "@/components/layout";
import {
  PageHeader,
  Button,
  ConfirmationModal,
  showToast,
} from "@/components/ui";
import { titleCase } from "@/lib/utils";
import { sellerDetailData } from "@/data/sellersMockData";
import { OrderInsightDrawer } from "@/components/orders/OrderInsightDrawer";
import {
  Ban,
  Pause,
  RotateCcw,
  CreditCard,
  UserCog,
  Trash2,
} from "lucide-react";
import { fmtDateShort } from "@/components/sellers/sellerDetail.utils";
import { InlineError } from "@/components/sellers/InlineError";
import { SellerFacilitators } from "@/components/sellers/SellerFacilitators";
import { SellerHeaderBadges } from "@/components/sellers/SellerHeaderBadges";
import { SellerTitle } from "@/components/sellers/SellerTitle";
import { SkeletonBar } from "@/components/sellers/SkeletonBar";
import { useAdminSeller } from "@/hooks/useAdminSeller";
import { SellerKpiGrid } from "@/components/sellers/SellerKpiGrid";
import { SellerTabNav } from "@/components/sellers/SellerTabNav";
import { OverviewTab } from "@/components/sellers/OverviewTab";
import { OrdersTab } from "@/components/sellers/OrdersTab";
import { InventoryTab } from "@/components/sellers/InventoryTab";
import { CatalogTab } from "@/components/sellers/CatalogTab";
import { SyncTab } from "@/components/sellers/SyncTab";
import { RevenueTab } from "@/components/sellers/RevenueTab";
import { ReportsTab } from "@/components/sellers/ReportsTab";
import { NotificationsTab } from "@/components/sellers/NotificationsTab";
import { ActivityTab } from "@/components/sellers/ActivityTab";

export default function SellerDetail() {
  const { id } = useParams();
  const { seller, loading, failed, notFound } = useAdminSeller(id);
  const name = seller?.name ?? "this seller";
  const d = sellerDetailData;

  const [tab, setTab] = useState("overview");
  const [modal, setModal] = useState(null);
  const [modalReason, setModalReason] = useState("");
  const [orderFilter, setOrderFilter] = useState("All");
  const [insightOrderId, setInsightOrderId] = useState(null);

  const handleAction = (action) => setModal(action);
  const confirmAction = () => {
    showToast.success(
      `${modal} confirmed`,
      `Action "${modal}" applied to ${name}.`,
    );
    setModal(null);
    setModalReason("");
  };

  const filteredOrders =
    orderFilter === "All"
      ? d.orders
      : d.orders.filter((o) => o.status === orderFilter);

  return (
    <DashboardLayout activePath="/sellers">
      <PageHeader
        title={<SellerTitle name={seller?.name} loading={loading} />}
        subtitle={
          loading ? (
            <SkeletonBar className="h-4 w-80" />
          ) : (
            seller &&
            `${seller.id}  ·  ${titleCase(seller.plan)}  ·  Joined ${fmtDateShort(seller.createdAt)}`
          )
        }
        breadcrumbs={[
          { label: "Sellers", active: false, href: "/sellers" },
          ...(seller ? [{ label: seller.name, active: true }] : []),
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="action-block"
              variant="outline"
              size="sm"
              onClick={() => handleAction("Block Seller")}
            >
              <Ban className="w-4 h-4" /> Block
            </Button>
            <Button
              data-testid="action-pause"
              variant="outline"
              size="sm"
              onClick={() => handleAction("Pause Seller")}
            >
              <Pause className="w-4 h-4" /> Pause
            </Button>
            <Button
              data-testid="action-reset-sync"
              variant="outline"
              size="sm"
              onClick={() => handleAction("Reset Sync")}
            >
              <RotateCcw className="w-4 h-4" /> Reset Sync
            </Button>
            <Button
              data-testid="action-change-plan"
              variant="outline"
              size="sm"
              onClick={() => handleAction("Change Plan")}
            >
              <CreditCard className="w-4 h-4" /> Plan
            </Button>
            <Button
              data-testid="action-impersonate"
              variant="secondary"
              size="sm"
              onClick={() =>
                showToast.info("Impersonate", `Viewing as ${name}...`)
              }
            >
              <UserCog className="w-4 h-4" /> Impersonate
            </Button>
            <Button
              data-testid="action-delete"
              variant="outline"
              size="sm"
              className="text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950"
              onClick={() => handleAction("Delete Seller")}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        }
      />

      {loading && <SkeletonBar className="mb-6 block h-[26px] w-64" />}
      {seller && <SellerHeaderBadges seller={seller} />}
      {failed && (
        <InlineError
          message="Could not load seller details. Please refresh the page."
          className="mb-6 max-w-none text-sm"
        />
      )}
      {notFound && (
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          No seller found with this id.
        </p>
      )}

      <SellerFacilitators workspaceId={id} />

      <SellerKpiGrid kpis={d.kpis} />

      <SellerTabNav tab={tab} onChange={setTab} />

      <div data-testid="tab-content">
        {tab === "overview" && <OverviewTab d={d} />}
        {tab === "orders" && (
          <OrdersTab
            orders={filteredOrders}
            orderFilter={orderFilter}
            onFilterChange={setOrderFilter}
            onViewOrder={setInsightOrderId}
          />
        )}
        {tab === "inventory" && <InventoryTab d={d} />}
        {tab === "catalog" && <CatalogTab d={d} />}
        {tab === "sync" && <SyncTab d={d} />}
        {tab === "revenue" && <RevenueTab d={d} />}
        {tab === "reports" && <ReportsTab />}
        {tab === "notifications" && <NotificationsTab d={d} />}
        {tab === "activity" && <ActivityTab d={d} />}
      </div>

      {/* Order Insight Drawer */}
      <OrderInsightDrawer
        open={!!insightOrderId}
        onClose={() => setInsightOrderId(null)}
        orderId={insightOrderId}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={!!modal}
        onOpenChange={(open) => {
          if (!open) setModal(null);
        }}
        title={modal || ""}
        description={`Are you sure you want to ${modal?.toLowerCase()} for ${name}? This action will be logged.`}
        confirmLabel={modal || "Confirm"}
        variant={
          modal === "Delete Seller" || modal === "Block Seller"
            ? "danger"
            : "primary"
        }
        requireReason
        reason={modalReason}
        onReasonChange={setModalReason}
        onConfirm={confirmAction}
        onCancel={() => setModal(null)}
      />
    </DashboardLayout>
  );
}
